import { SummarizedFeedbackInsightsIcon } from '@modules/oracle/components/OracleSearchSuggestions/SummarizedFeedbackInsightsIcon';
import {
  AutoFixHighRounded,
  ForumRounded,
  InsightsRounded,
} from '@mui/icons-material';
import {
  Card,
  CardActionArea,
  CardContent,
  Stack,
  Typography,
  alpha,
} from '@mui/material';
import { cyan, deepPurple, pink } from '@mui/material/colors';
import {
  AssistantSampleQuestion,
  AssistantSampleQuestionType,
} from 'generated/graphql';

interface OracleSearchSuggestionProps {
  suggestion: AssistantSampleQuestion;
  isLoading?: boolean;
  isDisabled?: boolean;
  onSelect: (suggestion: string) => void;
}

export function OracleSearchSuggestion({
  suggestion,
  isLoading,
  onSelect,
  isDisabled,
}: OracleSearchSuggestionProps) {
  const getIcon = () => {
    switch (suggestion.type) {
      case AssistantSampleQuestionType.Quantify:
        return (
          <InsightsRounded sx={{ fontSize: '18px', color: deepPurple[200] }} />
        );
      case AssistantSampleQuestionType.Summary:
        return <SummarizedFeedbackInsightsIcon sx={{ fontSize: '18px' }} />;
      case AssistantSampleQuestionType.Investigate:
        return <ForumRounded sx={{ fontSize: '18px', color: cyan[600] }} />;
      default:
        return (
          <AutoFixHighRounded sx={{ fontSize: '18px', color: pink[500] }} />
        );
    }
  };

  return (
    <Card
      variant="outlined"
      sx={{
        height: '100%',
        borderRadius: 2.5,
        minWidth: '140px',
        '--border-size': '1px',
        '--border-angle': '0turn',
        backgroundImage: (theme) =>
          `conic-gradient(from var(--border-angle), ${
            theme.palette.background.paper
          }, ${theme.palette.background.paper} 50%, ${
            theme.palette.background.paper
          }), conic-gradient(from var(--border-angle), transparent 20%, ${
            theme.palette.primary.main
          }, ${alpha(theme.palette.primary.main, 0)})`,
        backgroundSize: isLoading
          ? 'calc(100% - (var(--border-size) * 4)) calc(100% - (var(--border-size) * 4)), cover'
          : undefined,
        backgroundPosition: isLoading ? 'center center' : undefined,
        backgroundRepeat: isLoading ? 'no-repeat' : undefined,
        animation: 'bg-spin 3s linear infinite',
        cursor: isLoading ? 'progress' : isDisabled ? 'not-allowed' : 'pointer',
        '@keyframes bg-spin': {
          '100%': {
            '--border-angle': '1turn',
          },
        },
      }}
    >
      <CardActionArea
        sx={{ height: '100%' }}
        disabled={isLoading || isDisabled}
        onClick={() => onSelect(suggestion.question)}
      >
        <CardContent sx={{ p: 2 }}>
          <Stack spacing={0.5}>
            <Stack spacing={1} direction="row" alignItems="flex-start">
              <Stack mt={0.1}>{getIcon()}</Stack>
              <Typography
                variant="subtitle2"
                color={isDisabled ? 'text.disabled' : undefined}
              >
                {suggestion.header}
              </Typography>
            </Stack>
            <Typography
              variant="caption"
              color={isDisabled ? 'text.disabled' : 'text.secondary'}
              sx={{ pl: '26px' }}
            >
              {suggestion.question}
            </Typography>
          </Stack>
        </CardContent>
      </CardActionArea>
    </Card>
  );
}
